import { relationSchema } from "./relation";
import { projectSchema } from "./project";
import { userSchema } from "./user";
import { itemSchema } from "./item";
import { format } from "date-fns";
import { z } from "zod";

const invoiceRefSchema = z.object({
  id: z.string(),
  invoiceNumber: z.string(),
});

export const baseWorklogSchema = z.object({
  relationId: z.string({ invalid_type_error: "Relation ID must be a string" }),
  projectId: z.string({ invalid_type_error: "Project ID must be a string" }).optional().nullable(),
  itemId: z.string({ invalid_type_error: "Item ID must be a string" }).min(1, { message: "Item ID is required" }),
  assignedUserId: z
    .string({ invalid_type_error: "Assigned User ID must be a string" })
    .min(1, { message: "Assigned User ID is required" }),
  date: z.date({ invalid_type_error: "Date must be a valid date" }),
  startTime: z
    .string({ invalid_type_error: "Start time must be a string" })
    .min(1, { message: "Start time cannot be empty" }),
  endTime: z
    .string({ invalid_type_error: "End time must be a string" })
    .min(1, { message: "End time cannot be empty" }),
  description: z.string({ invalid_type_error: "Description must be a string" }).optional().nullable(),
  isBillable: z.boolean({ invalid_type_error: "Is billable must be a boolean" }),
});

export const createWorklogSchema = baseWorklogSchema;

export const updateWorklogSchema = baseWorklogSchema.partial().extend({
  id: z.string({ invalid_type_error: "ID must be a string" }),
});

export const getWorklogSchema = z.object({
  id: z.string({ invalid_type_error: "ID must be a string" }),
});

export const deleteWorklogSchema = z.object({
  id: z.string({ invalid_type_error: "ID must be a string" }),
});

export type ZodCreateWorklogSchema = z.infer<typeof createWorklogSchema>;
export type ZodUpdateWorklogSchema = z.infer<typeof updateWorklogSchema>;
export type ZodGetWorklogSchema = z.infer<typeof getWorklogSchema>;
export type ZodDeleteWorklogSchema = z.infer<typeof deleteWorklogSchema>;

export const worklogSchema = baseWorklogSchema.extend({
  id: z.string(),
  relation: relationSchema.optional().nullable(),
  project: projectSchema.optional().nullable(),
  item: itemSchema,
  assignedUser: userSchema,
  invoiceId: z.string().optional().nullable(),
  invoice: invoiceRefSchema.optional().nullable(),
  organizationId: z.string(),
  userId: z.string(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type WorklogSchema = z.infer<typeof worklogSchema>;

export const createWorklogSchemaDefaultValues: ZodCreateWorklogSchema = {
  relationId: "",
  projectId: null,
  itemId: "",
  assignedUserId: "",
  date: new Date(),
  startTime: format(new Date(), "HH:mm"),
  endTime: format(new Date(), "HH:mm"),
  description: "",
  isBillable: true,
};
